import { useParams } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useGetPublicProgress } from "@/api/orval";
import { activityVerb } from "@/lib/activity";

const LoadingSkeleton = () => (
  <div className="space-y-4">
    <div className="mb-4">
      <div className="flex items-start gap-3">
        <Skeleton className="h-10 w-10 rounded-full" />
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <Skeleton className="h-5 w-24" />
          </div>
          <Skeleton className="mt-1 h-14 rounded-2xl rounded-tl-none bg-primary/10 p-3" />
          <div className="mt-1 flex justify-end">
            <Skeleton className="h-3 w-12" />
          </div>
        </div>
      </div>
    </div>
    <Skeleton className="mb-4 h-2 w-full" />
    <div className="space-y-1 text-sm">
      <div className="flex justify-between">
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-4 w-32" />
      </div>
      <div className="flex justify-between">
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-4 w-32" />
      </div>
    </div>
  </div>
);

const NoProgress = ({ username, city }: { username: string; city: string }) => (
  <div className="flex flex-col items-center justify-center gap-2">
    <div className="text-lg font-medium">No streets explored yet</div>
    <div className="text-center text-sm text-muted-foreground">
      {username} hasn&apos;t explored any streets in {city} yet
    </div>
  </div>
);

export const PublicProgressCard = () => {
  const { username = "", city = "" } = useParams<{
    username: string;
    city: string;
  }>();

  const { data: progress, isLoading } = useGetPublicProgress(
    { username, city },
    { query: { enabled: !!username && !!city } },
  );

  if (!username || !city) {
    return null;
  }

  // Prefer the workspace's display name; fall back to the URL slug.
  const cityName = progress?.name ?? city;

  return (
    <div className="absolute left-4 top-4 z-20 w-full max-w-[calc(100vw-32px)] rounded-xl bg-white shadow sm:w-[415px]">
      <Card className="w-full">
        <CardContent className="pt-6">
          {isLoading ? (
            <LoadingSkeleton />
          ) : (
            <>
              {progress?.total_matched_streets === 0 ? (
                <NoProgress username={username} city={cityName} />
              ) : (
                <>
                  <div className="mb-4">
                    <div className="flex items-start gap-3">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={progress?.avatar ?? undefined} />
                        <AvatarFallback>
                          {username.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="font-medium">{username}</p>
                        </div>
                        <div className="mt-1 rounded-2xl rounded-tl-none bg-primary/10 p-3">
                          <p className="text-sm">
                            I&apos;m {progress?.progress_percentage.toFixed(1)}%
                            done with my project to{" "}
                            {activityVerb(progress?.activity_types)} every
                            street in {cityName}. See your own progress with{" "}
                            <a
                              href="https://everystreet.run"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary underline underline-offset-2 transition-colors hover:text-primary/70"
                            >
                              everystreet.run
                            </a>
                          </p>
                        </div>
                        <div className="flex justify-end">
                          <span className="text-xs text-muted-foreground">
                            Just now
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <Progress
                    value={progress?.progress_percentage}
                    className="mb-4 h-2"
                  />
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Streets</span>
                      <span>
                        {progress?.total_matched_streets.toLocaleString()} /{" "}
                        {progress?.total_streets.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Distance</span>
                      <span>
                        {(progress?.total_matched_km ?? 0).toFixed(1)} /{" "}
                        {progress?.total_network_km.toFixed(1)} km
                      </span>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
